import React from "react";
import "./App.css";
import { TeamsUserCredential } from "@microsoft/teamsfx";
import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
  Link
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { Providers, ProviderState } from "@microsoft/mgt-element";
import { TeamsFxProvider } from "@microsoft/mgt-teamsfx-provider";
import { CacheService } from "@microsoft/mgt";
import config from "./lib/config";
import Viewer from './Viewer';
import { checkPremiumAccess } from './CheckPremiumAccess';
import { app } from "@microsoft/teams-js";
import jwtDecode from 'jwt-decode'; // Install this package if not already installed

class Tab extends React.Component {
  constructor(props) {
    super(props);
    const cacheId = Providers.getCacheId();
    CacheService.clearCacheById(cacheId);
    this.state = {
      showLoginPage: undefined,
      urls: [],
      showLoginMessageBar: true,
      showInstructionMessageBar: true,
      hasPremiumAccess: true,
      context: null,
      grantedScopes: undefined,
      userEmail: undefined
    };
  }

  extractUrlParamsFromHash() {
    const hash = window.location.hash;
    const queryStart = hash.indexOf('?');
    let urls = [];
    let topicGuid = null;

    if (queryStart !== -1) {
      const queryString = hash.substring(queryStart + 1);
      const searchParams = new URLSearchParams(queryString);

      const urlsParam = searchParams.get('urls');
      if (urlsParam) {
        urls = urlsParam.split(',').map(u => decodeURIComponent(u));
      }

      const topicGuidParam = searchParams.get('topic_guid');
      if (topicGuidParam) {
        topicGuid = decodeURIComponent(topicGuidParam).trim() || null;
      }
    }

    return { urls, topicGuid };
  }

  extractTopicGuidFromSubPageId(subPageId) {
    if (!subPageId || typeof subPageId !== "string") {
      return null;
    }

    try {
      const decodedSubPageId = decodeURIComponent(subPageId);
      const queryStart = decodedSubPageId.indexOf("?");
      if (queryStart === -1) {
        return null;
      }

      const queryString = decodedSubPageId.substring(queryStart + 1);
      const params = new URLSearchParams(queryString);
      const rawTopicGuid = params.get("topic_guid");
      if (!rawTopicGuid) {
        return null;
      }

      return decodeURIComponent(rawTopicGuid).trim() || null;
    } catch (error) {
      console.warn("Unable to parse deep-link topic GUID from subPageId.", error);
      return null;
    }
  }

  async componentDidMount() {
    app.initialize().then(() => {
      app.getContext().then((context) => {
        this.setState({ context })
      });
    });

    const { onTabChange } = this.props;
    onTabChange({
      windowLocationHref: window.location.href,
    });

    this.scope = ["Files.Read.All"];

    this.credential = new TeamsUserCredential({
      initiateLoginEndpoint: config.initiateLoginEndpoint,
      clientId: config.clientId,
    });
    const provider = new TeamsFxProvider(this.credential, this.scope);
    Providers.globalProvider = provider;

    let consentNeeded = false;
    try {
      const tokenResponse = await this.credential.getToken(this.scope);
      const accessToken = tokenResponse.token;
      const decodedToken = jwtDecode(accessToken);
      const grantedScopes = decodedToken.scp ? decodedToken.scp.split(' ') : [];
      const userEmail = decodedToken.preferred_username || decodedToken.upn || decodedToken.email;
      this.setState({ grantedScopes, userEmail })
    } catch (error) {
      consentNeeded = true;
    }

    this.setState({
      showLoginPage: consentNeeded,
    });
    Providers.globalProvider.setState(
      consentNeeded ? ProviderState.SignedOut : ProviderState.SignedIn
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.context !== this.state.context ||
      prevState.grantedScopes !== this.state.grantedScopes
    ) {

      // Now check if both conditions are met
      if (this.state.context && this.state.grantedScopes) {
        const contextUser = this.state.context?.user;
        const email =
          this.state.userEmail ||
          contextUser?.loginHint ||
          contextUser?.userPrincipalName ||
          contextUser?.email ||
          "";

        setTimeout(() => {
          checkPremiumAccess(
            this.state.context?.user?.tenant?.id,
            this.state.context?.sharePointSite?.teamSiteDomain,
            this.state.grantedScopes,
            email,
            this.state.context)
            .then((result) => {
              console.log(result);
              this.setState({ hasPremiumAccess: result })
            });
        }, 0);
      }
    }
  }


  async loginBtnClick() {
    try {
      await this.credential.login(this.scope);
      const tokenResponse = await this.credential.getToken(this.scope);
      const accessToken = tokenResponse.token;
      const decodedToken = jwtDecode(accessToken);
      const grantedScopes = decodedToken.scp ? decodedToken.scp.split(' ') : [];
      const userEmail = decodedToken.preferred_username || decodedToken.upn || decodedToken.email;

      Providers.globalProvider.setState(ProviderState.SignedIn);
      this.setState({
        showLoginPage: false,
        showLoginMessageBar: false, // Optionally hide the MessageBar upon successful login
        grantedScopes,
        userEmail
      });
    } catch (err) {
      if (err.message?.includes("CancelledByUser")) {
        const helpLink = "https://aka.ms/teamsfx-auth-code-flow";
        err.message +=
          '\nIf you see "AADSTS50011: The reply URL specified in the request does not match the reply URLs configured for the application" ' +
          "in the popup window, you may be using unmatched version for TeamsFx SDK (version >= 0.5.0) and Teams Toolkit (version < 3.3.0) or " +
          `cli (version < 0.11.0). Please refer to the help link for how to fix the issue: ${helpLink}`;
      }
      alert("Login failed: " + err);
      return;
    }
  }

  render() {
    const { urls, topicGuid: hashTopicGuid } = this.extractUrlParamsFromHash();
    const contextTopicGuid = this.extractTopicGuidFromSubPageId(this.state.context?.page?.subPageId);
    const deepLinkTopicGuid = contextTopicGuid || hashTopicGuid;
    const { showLoginPage, showLoginMessageBar, showInstructionMessageBar } = this.state;

    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: 'hidden',
        }}
      >
        {/* First MessageBar: Login Prompt */}
        {showLoginPage && showLoginMessageBar && (
          <MessageBar className="messagebar">
            <MessageBarBody>
              <MessageBarTitle>
                {this.scope.includes("Files.ReadWrite.All")
                  ? "No write permissions to your SharePoint and Teams: "
                  : "Not connected to your SharePoint and Teams: "}
              </MessageBarTitle>
              {"Please connect to view your IFC files."}
              <br />
              <Button
                appearance="primary"
                onClick={() => this.loginBtnClick()}
                style={{ marginTop: 10 }}
              >
                Connect
              </Button>
            </MessageBarBody>
            {/* Close Button */}
            <MessageBarActions
              containerAction={
                <Button
                  aria-label="dismiss"
                  appearance="transparent"
                  icon={<DismissRegular />}
                />
              }
            >
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={() => this.setState({ showLoginMessageBar: false })}
              />
            </MessageBarActions>
          </MessageBar>
        )}

        {/* Second MessageBar: Instructions */}
        {!showLoginPage && urls.length === 0 && showInstructionMessageBar && (
          <MessageBar className="messagebar">
            <MessageBarBody>
              <MessageBarTitle>Load your files directly from SharePoint and Teams:</MessageBarTitle>
              Click the dropdown arrow by the tab name, then paste your SharePoint links in the Teams tab settings to save the IFC files your team wants to view in this tab.{" "}
              <Link target="_blank" href="https://docs.flinker.app/">Learn more</Link>
            </MessageBarBody>
            {/* Close Button */}
            <MessageBarActions
              containerAction={
                <Button
                  aria-label="dismiss"
                  appearance="transparent"
                  icon={<DismissRegular />}
                  onClick={() => this.setState({ showInstructionMessageBar: false })}
                />
              }
            >
            </MessageBarActions>
          </MessageBar>
        )}

        {/* Main Content */}
        <Viewer
          auth={showLoginPage !== true}
          urls={urls}
          deepLinkTopicGuid={deepLinkTopicGuid}
          teamSiteUrl={this.state.context?.sharePointSite?.teamSiteUrl}
          relativeUrl={this.state.context?.channel?.relativeUrl}
          hasPremiumAccess={this.state.hasPremiumAccess}
          context={this.state.context}
        />
      </div>
    );
  }
}

export default Tab;
